/**
 * Date utilities compartilhadas — base UTC PURA, promovida de
 * `pages/operacoes/utils/dateUtils.js` (que hoje é re-export deste módulo).
 *
 * CONTRATO (herdado do /operacoes):
 *   - "Data" no sistema é sempre `YYYY-MM-DD` UTC. Não tem time, não tem TZ.
 *   - Backend bucketa por `getUTCDate()`; worker converte `date_created` do
 *     ML pra UTC ao salvar. Frontend interpreta filtros como UTC dates.
 *   - Toda função aqui usa `getUTC*`/`Date.UTC(...)` ou string-slicing de ISO
 *     (zero risco de TZ leak). NUNCA use `getFullYear/getMonth` local pra
 *     montar `YYYY-MM-DD` — é o bug de off-by-one apontado no doc E-5.
 *
 * DOIS formatadores de display coexistem DE PROPÓSITO (doc E clusters 2/5/6):
 *   - `FormatIsoDate` / `FormatDate`: DATA ABSTRATA (`YYYY-MM-DD` de venda,
 *     período, bucket) → "DD/MM/YYYY" via slicing. Sem hora, sem fuso.
 *   - `FormatDateTimeBr`: INSTANTE (timestamp de evento: created_at, login,
 *     movimentação) → wall-clock pt-BR no fuso de Brasília, "DD/MM/AAAA HH:mm".
 * Não troque um pelo outro: datas abstratas não têm hora; instantes precisam
 * do fuso explícito (backend serializa UTC, display SEMPRE Brasília).
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const BR_DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

export function Pad2(n) {
    return String(n).padStart(2, '0');
}

/**
 * Date → "YYYY-MM-DD" no UTC. Aceita Date object ou string ISO.
 * Strings ISO sem 'Z' são interpretadas como UTC (não local).
 */
export function ToIsoDate(date) {
    if (typeof date === 'string') {
        const m = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    }
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getUTCFullYear()}-${Pad2(d.getUTCMonth() + 1)}-${Pad2(d.getUTCDate())}`;
}

/** "YYYY-MM-DD" → Date UTC 00:00. */
export function ParseIsoDate(iso) {
    if (!iso) return null;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0));
}

/** ISO date string → "DD/MM/YYYY". Pure string ops, zero risco de TZ. */
export function FormatDate(iso) {
    if (!iso) return '';
    if (iso instanceof Date) {
        if (Number.isNaN(iso.getTime())) return '';
        return `${Pad2(iso.getUTCDate())}/${Pad2(iso.getUTCMonth() + 1)}/${iso.getUTCFullYear()}`;
    }
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    return `${m[3]}/${m[2]}/${m[1]}`;
}

/** ISO date string → "DD/MM". */
export function FormatDateShort(iso) {
    if (!iso) return '';
    if (iso instanceof Date) {
        if (Number.isNaN(iso.getTime())) return '';
        return `${Pad2(iso.getUTCDate())}/${Pad2(iso.getUTCMonth() + 1)}`;
    }
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    return `${m[3]}/${m[2]}`;
}

/**
 * DATA ABSTRATA com guard: "YYYY-MM-DD…" → "DD/MM/YYYY"; vazio/inválido →
 * `blank` ('—'). Use pra datas de negócio (venda, período), NÃO pra timestamps.
 */
export function FormatIsoDate(iso, { blank = '—' } = {}) {
    return FormatDate(iso) || blank;
}

/**
 * INSTANTE (timestamp) → wall-clock pt-BR em Brasília: "DD/MM/AAAA, HH:mm".
 * Vazio/inválido → `blank` ('—'). timeZone explícito — não depende do fuso do
 * browser (convenção: backend serializa UTC, display SEMPRE Brasília).
 */
export function FormatDateTimeBr(value, { blank = '—' } = {}) {
    if (!value) return blank;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return blank;
    return BR_DATE_TIME.format(d);
}

/** Aliases legados — mesma função (todos os displays usam UTC agora). */
export const FormatBrDate = FormatDate;
export const FormatBrDateShort = FormatDateShort;

/** Dias inclusivos entre 2 datas. */
export function DaysBetween(from, to) {
    const a = from instanceof Date ? from : ParseIsoDate(from);
    const b = to instanceof Date ? to : ParseIsoDate(to);
    if (!a || !b) return 0;
    return Math.floor((b.getTime() - a.getTime()) / MS_PER_DAY) + 1;
}

/** Soma N dias (UTC). */
export function AddDays(date, n) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + n);
    return d;
}

/**
 * "Hoje" UTC. Recebe opcionalmente um `anchor` (ISO string ou Date) — útil
 * pra usar o `asOf` do backend e não depender do relógio do navegador.
 */
export function TodayUtc(anchor = null) {
    const ref = anchor ? new Date(anchor) : new Date();
    return ToIsoDate(ref);
}

/** Lista de ISO dates entre [from, to] inclusivo. */
export function EnumerateDates(from, to) {
    const start = from instanceof Date ? from : ParseIsoDate(from);
    const end = to instanceof Date ? to : ParseIsoDate(to);
    if (!start || !end) return [];
    const out = [];
    let cursor = new Date(start);
    while (cursor.getTime() <= end.getTime()) {
        out.push(ToIsoDate(cursor));
        cursor = AddDays(cursor, 1);
    }
    return out;
}

/** ISO date - N dias (retorna ISO string). */
export function MinusDaysIso(iso, n) {
    if (!iso) return null;
    const d = ParseIsoDate(iso);
    if (!d) return null;
    return ToIsoDate(AddDays(d, -n));
}

// ── Datas de RH (decisão do dono no aceite do M0) ───────────────────────────
// Listagem de funcionários NÃO mostra a data crua: admissão vira "há quanto
// tempo trabalha aqui" e nascimento vira "quanto falta pro aniversário" — na
// MAIOR unidade, no padrão dos exemplos do dono ("1 ano/6 meses/1 dia").

/** Meses COMPLETOS entre duas datas UTC (dia do mês corta o mês incompleto). */
function fullMonthsBetweenUtc(from, to) {
    let months = (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth());
    if (to.getUTCDate() < from.getUTCDate()) {
        months -= 1;
    }
    return months;
}

function pluralUnit(n, singular, plural) {
    return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Tempo de casa desde a admissão: "3 anos", "1 ano", "6 meses", "1 dia",
 * "hoje". Uma unidade só (a maior), como o dono pediu. Inválido/futuro → blank.
 */
export function FormatTempoDeCasa(hiredAt, { anchor = null, blank = '—' } = {}) {
    const from = hiredAt instanceof Date ? hiredAt : ParseIsoDate(ToIsoDate(hiredAt));
    const to = anchor ? new Date(anchor) : new Date();
    if (!from || Number.isNaN(from.getTime()) || from.getTime() > to.getTime()) {
        return blank;
    }
    const months = fullMonthsBetweenUtc(from, to);
    if (months >= 12) {
        return pluralUnit(Math.floor(months / 12), 'ano', 'anos');
    }
    if (months >= 1) {
        return pluralUnit(months, 'mês', 'meses');
    }
    const days = Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY);
    if (days < 1) {
        return 'hoje';
    }
    return pluralUnit(days, 'dia', 'dias');
}

/**
 * Contagem regressiva para o PRÓXIMO aniversário: "em 4 meses", "em 15 dias",
 * "amanhã", "hoje". 29/02 em ano não bissexto conta como 28/02.
 */
export function FormatProximoAniversario(birthDate, { anchor = null, blank = '—' } = {}) {
    const birth = birthDate instanceof Date ? birthDate : ParseIsoDate(ToIsoDate(birthDate));
    if (!birth || Number.isNaN(birth.getTime())) {
        return blank;
    }
    const now = anchor ? new Date(anchor) : new Date();
    const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

    const nextFor = (year) => {
        const month = birth.getUTCMonth();
        const day = birth.getUTCDate();
        const candidate = new Date(Date.UTC(year, month, day));
        // 29/02 vira 28/02 quando o ano-alvo não é bissexto (Date rolaria p/ 01/03).
        if (candidate.getUTCMonth() !== month) {
            return new Date(Date.UTC(year, month, day - 1));
        }
        return candidate;
    };

    let next = nextFor(todayUtc.getUTCFullYear());
    if (next.getTime() < todayUtc.getTime()) {
        next = nextFor(todayUtc.getUTCFullYear() + 1);
    }

    const days = Math.round((next.getTime() - todayUtc.getTime()) / MS_PER_DAY);
    if (days === 0) {
        return 'hoje';
    }
    if (days === 1) {
        return 'amanhã';
    }
    const months = fullMonthsBetweenUtc(todayUtc, next);
    if (months >= 1) {
        return `em ${pluralUnit(months, 'mês', 'meses')}`;
    }
    return `em ${pluralUnit(days, 'dia', 'dias')}`;
}
